import { NordEvent } from '../common/events.js'
import { immutableResizableLayout } from './_immutable-resizable-layout.js'

export type ResizableResizeSource = 'pointer' | 'keyboard' | 'programmatic'

export interface ResizableLayoutItem {
  readonly id: string
  readonly size: number
}

abstract class ResizableLifecycleEvent extends NordEvent {
  declare readonly layout: readonly ResizableLayoutItem[]
  declare readonly source: ResizableResizeSource

  protected constructor(
    type: string,
    layout: readonly ResizableLayoutItem[],
    source: ResizableResizeSource,
    cancelable = false,
  ) {
    super(type, { cancelable })
    Object.defineProperties(this, {
      layout: {
        value: immutableResizableLayout(layout),
        enumerable: true,
        writable: false,
      },
      source: {
        value: source,
        enumerable: true,
        writable: false,
      },
    })
  }
}

/** Event dispatched before an accepted pointer drag starts. */
export class ResizableResizeStartEvent extends ResizableLifecycleEvent {
  static eventName = 'nord-resize-start'

  constructor(layout: readonly ResizableLayoutItem[], source: ResizableResizeSource) {
    super(ResizableResizeStartEvent.eventName, layout, source, true)
  }
}

/** Cancelable event carrying one proposed or committed ordered layout. */
export class ResizableResizeEvent extends ResizableLifecycleEvent {
  static eventName = 'nord-resize'

  constructor(layout: readonly ResizableLayoutItem[], source: ResizableResizeSource) {
    super(ResizableResizeEvent.eventName, layout, source, true)
  }
}

/** Event dispatched when an accepted pointer drag terminates for any reason. */
export class ResizableResizeEndEvent extends ResizableLifecycleEvent {
  static eventName = 'nord-resize-end'

  constructor(layout: readonly ResizableLayoutItem[], source: ResizableResizeSource) {
    super(ResizableResizeEndEvent.eventName, layout, source)
  }
}

declare global {
  interface HTMLElementEventMap {
    'nord-resize-start': ResizableResizeStartEvent
    'nord-resize': ResizableResizeEvent
    'nord-resize-end': ResizableResizeEndEvent
  }
  interface DocumentEventMap {
    'nord-resize-start': ResizableResizeStartEvent
    'nord-resize': ResizableResizeEvent
    'nord-resize-end': ResizableResizeEndEvent
  }
  interface WindowEventMap {
    'nord-resize-start': ResizableResizeStartEvent
    'nord-resize': ResizableResizeEvent
    'nord-resize-end': ResizableResizeEndEvent
  }
}
